<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

export type StatusColor = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral'

interface Props {
  /**
   * Semantic color. Drives bg (light tint), text (default shade), and
   * the leading dot. Use `neutral` for unknown / fallback states.
   */
  color: StatusColor
  /** Label text — typically the human-readable status (e.g. "Verified"). */
  label: string
  /** Hide the leading dot — text-only pill. */
  hideDot?: boolean
}

withDefaults(defineProps<Props>(), {
  hideDot: false,
})
</script>

<template>
  <Box class="status-badge" :class="[`status-badge--${color}`]">
    <Box v-if="!hideDot" class="status-badge__dot" />
    <Text type="caption" weight="medium" class="status-badge__label">
      {{ label }}
    </Text>
  </Box>
</template>

<style scoped>
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--sb-bg);
  color: var(--sb-text);
  line-height: 1;
  white-space: nowrap;
}

.status-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--sb-dot);
  flex-shrink: 0;
}

.status-badge__label {
  /* inherit color from parent (--sb-text via .status-badge) */
  color: inherit;
}

.status-badge--primary {
  --sb-bg: var(--p-primary-50);
  --sb-text: var(--p-primary-600);
  --sb-dot: var(--p-primary-600);
}
.status-badge--success {
  --sb-bg: var(--p-success-50);
  --sb-text: var(--p-success-600);
  --sb-dot: var(--p-success-600);
}
.status-badge--warning {
  --sb-bg: var(--p-warning-50);
  --sb-text: var(--p-warning-600);
  --sb-dot: var(--p-warning-600);
}
.status-badge--danger {
  --sb-bg: var(--p-danger-50);
  --sb-text: var(--p-danger-600);
  --sb-dot: var(--p-danger-600);
}
.status-badge--info {
  --sb-bg: var(--p-info-50);
  --sb-text: var(--p-info-600);
  --sb-dot: var(--p-info-600);
}
.status-badge--neutral {
  --sb-bg: var(--p-surface-50);
  --sb-text: var(--p-text-muted-color);
  --sb-dot: var(--p-text-muted-color);
}
</style>
